import 'react-datepicker/dist/react-datepicker.css';

import { cs, sk, enGB } from 'date-fns/locale';
import { useMemo } from 'react';
import { DatePicker } from 'utils/datePickerImport';
import type { FieldValues } from 'react-hook-form';
import { parseISO, isValid, parse, set } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { InputLabel, styled, Typography } from '@mui/material';
import { FilterInputWrapper as Wrapper } from './FilterInputWrapper';
import { StyledFilterInput as StyledDatePicker } from './StyledFilterInput';

const DatePickerWrapper = styled('div')(() => ({
  display: 'flex',
  flex: 1,
  height: '100%',
  width: '100%',
  '& .react-datepicker': {
    display: 'flex',
  },
  '& .react-datepicker-popper': {
    zIndex: 100000,
  },
  '& .react-datepicker-wrapper': {
    width: '100%',
    height: '100%',
  },

  '& .react-datepicker__input-container input': {
    width: '100%',
    padding: '10px',
    height: '100%',
    outline: 'none',
    cursor: 'pointer',
    borderRadius: '4px',
  },
}));

interface DateInputProps {
  value: string;
  label: string;
  error?: FieldValues['errors'];
  dateMask?: DateMask;
  disabled?: boolean;
  layout?: 'full' | 'half';
  onDateChange?: () => void;
  onBlur?: () => void;
  onChange?: (date: Date | null) => void;
}

type DateMask = {
  Year: boolean;
  Month: boolean;
  Day: boolean;
  Time: boolean;
};

export const FilterDynamicDateInput = ({ value, label, error, onDateChange, onChange, dateMask, disabled, layout = 'full' }: DateInputProps) => {
  const { i18n } = useTranslation();

  const locale = useMemo(() => {
    switch (i18n.language) {
      case 'cs':
        return cs;
      case 'sk':
        return sk;
      case 'en':
        return enGB;
      default:
        return cs;
    }
  }, [i18n.language]);

  const time = useMemo(() => (dateMask?.Time ? ' HH:mm' : ''), [dateMask]);

  const dateFormat = useMemo(
    () =>
      dateMask?.Day
        ? 'dd.MM.yyyy' + time
        : dateMask?.Month && dateMask?.Year
          ? 'MM.yyyy' + time
          : dateMask?.Year
            ? 'yyyy' + time
            : dateMask?.Month
              ? 'MMMM' + time
              : time,
    [dateMask, time]
  );

  const showFullMonthYearPicker = useMemo(() => dateMask?.Year && dateMask?.Month && dateMask?.Day, [dateMask]);
  const showMonthYearPicker = useMemo(() => !dateMask?.Year && dateMask?.Month && !dateMask?.Day, [dateMask]);
  const showYearPicker = useMemo(() => dateMask?.Year && !dateMask?.Month && !dateMask?.Day, [dateMask]);

  const shouldSetDefaultTime = useMemo(() => {
    return !dateMask?.Time && (dateMask?.Day || dateMask?.Month || dateMask?.Year);
  }, [dateMask]);

  const parseDate = (dateString: string): Date | null => {
    if (!dateString.trim()) return null;
    const isoDate = parseISO(dateString);
    if (isValid(isoDate)) {
      return isoDate;
    }

    const parsedDate = parse(dateString, dateFormat, new Date(), { locale });
    if (isValid(parsedDate)) {
      return parsedDate;
    }

    return null;
  };

  const setDefaultTimeIfNeeded = (date: Date | null): Date | null => {
    if (date && shouldSetDefaultTime) {
      return set(new Date(date), { hours: 12, minutes: 0, seconds: 0, milliseconds: 0 });
    }
    return date;
  };

  const handleChange = (e?: Date | null | React.ChangeEvent<HTMLInputElement>, dateChangeHandler?: (date: Date | null) => void) => {
    let dateValue: string | undefined;
    if (e instanceof Date) {
      dateValue = e.toISOString();
    } else if (e && 'target' in e) {
      dateValue = e.target.value;
    }

    if (dateValue && dateValue?.trim() !== '') {
      const parsedDate = parseDate(dateValue);
      const dateWithDefaultTime = setDefaultTimeIfNeeded(parsedDate);

      if (dateWithDefaultTime && isValid(dateWithDefaultTime)) {
        dateChangeHandler?.(dateWithDefaultTime);
        onDateChange?.();
      } else {
        dateChangeHandler?.(null);
      }
    }

    if (!dateValue) {
      dateChangeHandler?.(null);
    }
  };

  return (
    <Wrapper layout={layout}>
      <InputLabel>{label}</InputLabel>

      <StyledDatePicker hasError={!!error}>
        <DatePickerWrapper>
          <DatePicker
            disabled={disabled}
            locale={locale}
            autoComplete="off"
            timeFormat="HH:mm"
            placeholderText=""
            timeIntervals={1}
            dateFormat={dateFormat}
            showFullMonthYearPicker={showFullMonthYearPicker}
            showMonthYearPicker={showMonthYearPicker}
            showYearPicker={showYearPicker}
            showMonthDropdown={dateMask?.Month}
            showYearDropdown={dateMask?.Year}
            showTimeSelect={dateMask?.Time}
            showTimeSelectOnly={dateMask?.Time && !dateMask?.Day && !dateMask?.Month && !dateMask?.Year}
            isClearable
            strictParsing={false}
            allowSameDay={true}
            onChange={(e: Date | null) => handleChange(e, onChange)}
            selected={value ? new Date(value) : null}
            popperPlacement="bottom-start"
            withPortal
          />
        </DatePickerWrapper>
      </StyledDatePicker>
      {error && (
        <Typography variant="body1" color="error">
          {error.message}
        </Typography>
      )}
    </Wrapper>
  );
};
